"use client";

import { useEffect, useRef, useState } from "react";
import { CONFIG } from "./config";
import { SVGS } from "./characters";

type Phase = "idle" | "prep" | "count" | "preview" | "deliver";

const svgURL = (s: string) =>
  "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(s)));
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const srcDe = (nombre: string, img?: string) =>
  img && img.length ? img : svgURL(SVGS[nombre] ?? SVGS.fantasma);
const offDe = (entra: string) =>
  ({ left: "-40vw,0", right: "40vw,0", bottom: "0,40vh", top: "0,-40vh" } as Record<string, string>)[
    entra
  ] ?? "0,40vh";

export default function Booth() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [count, setCount] = useState<number | null>(null);
  const [charsIn, setCharsIn] = useState(false);
  const [shot, setShot] = useState("");
  const [demo, setDemo] = useState(false);
  const [flash, setFlash] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const charImgs = useRef<Record<string, HTMLImageElement>>({});
  const hasCam = useRef(false);
  const streamRef = useRef<MediaStream | null>(null);

  // Precarga de imágenes de personajes (para dibujarlas en la captura) + cámara
  useEffect(() => {
    CONFIG.personajes.forEach((p) => {
      const im = new Image();
      im.src = srcDe(p.nombre, p.img);
      charImgs.current[p.nombre] = im;
    });
    initCam();
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function initCam(): Promise<boolean> {
    if (hasCam.current) return true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: CONFIG.camara.facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current!;
      v.srcObject = stream;
      await v.play();
      v.style.transform = CONFIG.camara.espejo ? "scaleX(-1)" : "none";
      hasCam.current = true;
      setDemo(false);
      return true;
    } catch {
      hasCam.current = false;
      setDemo(true);
      return false;
    }
  }

  async function run() {
    // En iPad/Safari la cámara arranca con el toque del usuario (gesto)
    await initCam();
    setPhase("prep");
    setCharsIn(true);
    await sleep(1100);
    setPhase("count");
    for (let n = CONFIG.countdownSegundos; n >= 1; n--) {
      setCount(n);
      await sleep(1000);
    }
    setCount(null);
    setFlash(true);
    const data = capture();
    await sleep(260);
    setFlash(false);
    setShot(data);
    setCharsIn(false);
    setPhase("preview");
  }

  function capture(): string {
    const W = 1280;
    const H = Math.round(W * (window.innerHeight / window.innerWidth));
    const cv = document.createElement("canvas");
    cv.width = W;
    cv.height = H;
    const ctx = cv.getContext("2d")!;
    const v = videoRef.current;

    // Fondo: cámara (espejada, igual que el preview) o backdrop demo
    if (hasCam.current && v && v.videoWidth) {
      const vw = v.videoWidth,
        vh = v.videoHeight;
      const scale = Math.max(W / vw, H / vh),
        dw = vw * scale,
        dh = vh * scale;
      ctx.save();
      if (CONFIG.camara.espejo) {
        ctx.translate(W, 0);
        ctx.scale(-1, 1);
      }
      ctx.drawImage(v, (W - dw) / 2, (H - dh) / 2, dw, dh);
      ctx.restore();
    } else {
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, "#1a1322");
      g.addColorStop(1, "#0a0710");
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
    }

    // Personajes en sus posiciones
    CONFIG.personajes.forEach((p) => {
      const img = charImgs.current[p.nombre];
      if (!img || !img.complete || !img.naturalWidth) return;
      const w = W * (p.widthPct / 100);
      const h = w * (img.naturalHeight / img.naturalWidth);
      ctx.drawImage(img, W * (p.leftPct / 100), H * (p.topPct / 100), w, h);
    });

    // Viñeta + marco + lockup (el "entorno")
    const vg = ctx.createRadialGradient(W / 2, H * 0.4, H * 0.3, W / 2, H / 2, H * 0.8);
    vg.addColorStop(0, "rgba(0,0,0,0)");
    vg.addColorStop(1, "rgba(0,0,0,.7)");
    ctx.fillStyle = vg;
    ctx.fillRect(0, 0, W, H);
    ctx.strokeStyle = "rgba(255,138,61,.5)";
    ctx.lineWidth = 5;
    ctx.strokeRect(16, 16, W - 32, H - 32);
    ctx.fillStyle = "#fff";
    ctx.font = "800 " + Math.round(W * 0.03) + "px system-ui";
    ctx.textAlign = "center";
    ctx.shadowColor = "rgba(0,0,0,.7)";
    ctx.shadowBlur = 8;
    ctx.fillText(CONFIG.lockup.replace(/<[^>]+>/g, ""), W / 2, H - 30);

    return cv.toDataURL("image/jpeg", 0.92);
  }

  async function enviarWhatsApp() {
    // TODO: enlazar al webhook de n8n (igual que Cofiño). Stub por ahora.
    try {
      await fetch("/api/booth/deliver", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: shot, canal: "whatsapp" }),
      });
      alert("Enviado (stub). Conectar el webhook de n8n para el envío real.");
    } catch {
      alert("No se pudo enviar.");
    }
  }

  function descargar() {
    const a = document.createElement("a");
    a.href = shot;
    a.download = "fabrica-embrujada.jpg";
    a.click();
  }

  const titulo = CONFIG.campanaTitulo;

  return (
    <div className="stage" style={{ ["--accent" as any]: CONFIG.colorPrimario }}>
      {/* cámara en vivo */}
      <video ref={videoRef} className={demo ? "video" : "video on"} autoPlay playsInline muted />
      {demo && (
        <div className="demo">
          <div className="floor" />
        </div>
      )}

      {/* personajes */}
      <div className="chars">
        {CONFIG.personajes.map((p, i) => {
          const off = offDe(p.entra).split(",");
          return (
            <div
              key={i}
              className={`char ${charsIn ? "in bob" : ""}`}
              style={{
                left: p.leftPct + "%",
                top: p.topPct + "%",
                width: p.widthPct + "%",
                transform: `translate(${off[0]},${off[1]}) scale(.85)`,
              }}
            >
              <img src={srcDe(p.nombre, p.img)} alt={p.nombre} />
            </div>
          );
        })}
      </div>

      {/* entorno */}
      <div className="framefx">
        <div className="edge" />
      </div>
      <div className="lockup" dangerouslySetInnerHTML={{ __html: CONFIG.lockup }} />

      {/* IDLE */}
      {phase === "idle" && (
        <div className="screen on idle">
          <div className="title">
            <span>{titulo[0]}</span>
            <b>{titulo[1]}</b>
          </div>
          <div className="sub">Posá con los personajes y llevate tu foto embrujada</div>
          <button className="bigbtn" onClick={run}>
            TOCA PARA TU FOTO
          </button>
        </div>
      )}

      {/* PREP + COUNT */}
      {(phase === "prep" || phase === "count") && (
        <div className="screen on prep">
          {phase === "prep" && <div className="prepMsg show">¡Prepárate!</div>}
          {count !== null && (
            <div key={count} className="count tick">
              {count}
            </div>
          )}
        </div>
      )}

      {/* FLASH */}
      <div className={`flash ${flash ? "go" : ""}`} />

      {/* PREVIEW */}
      {phase === "preview" && (
        <div className="screen on preview">
          <img className="shot" src={shot} alt="tu foto" />
          <div className="row">
            <button className="btn ghost" onClick={() => setPhase("idle")}>
              ↻ Otra foto
            </button>
            <button className="btn primary" onClick={() => setPhase("deliver")}>
              Recibir mi foto
            </button>
          </div>
        </div>
      )}

      {/* DELIVERY */}
      {phase === "deliver" && (
        <div className="screen on deliver">
          <h2>¡Tu foto está lista! 🎃</h2>
          <p>Escaneá el QR o te la mandamos por WhatsApp</p>
          <QR />
          <div className="row">
            {CONFIG.entrega.whatsapp && (
              <button className="btn wa" onClick={enviarWhatsApp}>
                Enviar por WhatsApp
              </button>
            )}
            <button className="btn ghost" onClick={() => setPhase("idle")}>
              Listo
            </button>
          </div>
        </div>
      )}

      {demo && <div className="badge">MODO DEMO · sin cámara</div>}

      <style jsx>{styles}</style>
    </div>
  );
}

/* QR placeholder decorativo (se reemplaza por el QR real de la entrega) */
function QR() {
  const seed = new Set([0, 2, 4, 6, 8, 10, 12, 16, 18, 20, 24, 28, 30, 32, 36, 40, 44, 48, 52, 56, 60, 64, 68, 72, 76, 80]);
  return (
    <div className="qr">
      {Array.from({ length: 81 }).map((_, i) => {
        const dark = i < 9 || i > 71 || i % 9 === 0 || i % 9 === 8 || seed.has(i);
        return <i key={i} style={{ background: dark ? "#0d0b12" : "#fff" }} />;
      })}
      <style jsx>{`
        .qr {
          width: 210px;
          height: 210px;
          background: #fff;
          border-radius: 14px;
          display: grid;
          grid-template: repeat(9, 1fr) / repeat(9, 1fr);
          padding: 14px;
          gap: 3px;
        }
        .qr i {
          border-radius: 2px;
        }
      `}</style>
    </div>
  );
}

const styles = `
  .stage {
    position: fixed; inset: 0; overflow: hidden; color: #fff;
    font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
    user-select: none; -webkit-user-select: none;
    background: radial-gradient(130% 100% at 50% 0%, #2a1c3a 0%, #17111f 45%, #0b0810 100%);
  }
  .video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
    transform: scaleX(-1); background: #0b0810; opacity: 0; transition: opacity .4s; }
  .video.on { opacity: 1; }
  .demo { position: absolute; inset: 0;
    background: radial-gradient(60% 50% at 50% 38%, rgba(124,58,237,.25), transparent 70%),
      repeating-linear-gradient(90deg, #1a1322 0 42px, #160f1e 42px 84px); }
  .demo .floor { position: absolute; left: 0; right: 0; bottom: 0; height: 34%;
    background: linear-gradient(#0000, #0a0710); border-top: 2px solid #2a1f38; }
  .framefx { position: absolute; inset: 0; pointer-events: none;
    box-shadow: inset 0 0 180px 40px rgba(0,0,0,.72); }
  .framefx .edge { position: absolute; inset: 14px; border: 3px solid rgba(255,138,61,.35); border-radius: 14px; }
  .char { position: absolute; pointer-events: none; opacity: 0;
    filter: drop-shadow(0 12px 20px rgba(0,0,0,.5));
    transition: transform .6s cubic-bezier(.2,.8,.2,1), opacity .5s; }
  .char :global(img) { width: 100%; height: 100%; display: block; }
  .char.in { opacity: 1; transform: translate(0,0) scale(1) !important; }
  .char.bob { animation: bob 2.4s ease-in-out infinite; }
  @keyframes bob { 0%,100% { margin-top: 0 } 50% { margin-top: -14px } }
  .lockup { position: absolute; left: 0; right: 0; bottom: 22px; text-align: center;
    font-weight: 800; letter-spacing: 3px; font-size: clamp(14px,2.4vw,24px);
    text-shadow: 0 2px 14px rgba(0,0,0,.7); }
  .lockup :global(b) { color: #ff8a3d; }
  .screen { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
    flex-direction: column; text-align: center; z-index: 20; }
  .idle { background: radial-gradient(120% 90% at 50% 10%, rgba(124,58,237,.18), transparent 60%); }
  .idle .title { font-size: clamp(30px,6vw,64px); font-weight: 900; line-height: 1.02;
    text-shadow: 0 3px 24px rgba(242,103,34,.5); }
  .idle .title :global(b) { color: var(--accent); display: block; }
  .idle .sub { margin: 14px 0 40px; color: #cdbfe0; font-size: clamp(14px,2.2vw,20px); letter-spacing: 1px; }
  .bigbtn { background: var(--accent); color: #fff; border: none; font-weight: 900;
    font-size: clamp(18px,3vw,28px); letter-spacing: 1.5px; padding: 22px 60px; border-radius: 60px;
    cursor: pointer; box-shadow: 0 14px 40px rgba(242,103,34,.55); animation: pulse 1.8s infinite; }
  .bigbtn:active { transform: scale(.97); }
  @keyframes pulse { 0%,100% { box-shadow: 0 14px 40px rgba(242,103,34,.5) }
    50% { box-shadow: 0 14px 60px rgba(242,103,34,.85) } }
  .prepMsg { font-size: clamp(26px,5vw,52px); font-weight: 900; color: #fff;
    text-shadow: 0 3px 20px rgba(0,0,0,.6); animation: popMsg .5s ease forwards; }
  @keyframes popMsg { from { opacity: 0; transform: scale(.6) } to { opacity: 1; transform: scale(1) } }
  .count { font-size: clamp(120px,26vw,300px); font-weight: 900; color: #ff8a3d;
    text-shadow: 0 0 50px rgba(255,138,61,.8); animation: tick 1s ease-out; }
  @keyframes tick { 0% { opacity: 0; transform: scale(2.2) } 30% { opacity: 1; transform: scale(1) }
    100% { opacity: 0; transform: scale(.7) } }
  .flash { position: absolute; inset: 0; background: #fff; opacity: 0; z-index: 30; pointer-events: none; }
  .flash.go { animation: flash .5s ease-out; }
  @keyframes flash { 0% { opacity: 0 } 10% { opacity: .95 } 100% { opacity: 0 } }
  .preview { background: rgba(6,4,10,.9); z-index: 25; }
  .preview .shot { max-width: 72%; max-height: 66%; border: 6px solid #fff; border-radius: 6px;
    box-shadow: 0 20px 60px rgba(0,0,0,.6); }
  .row { display: flex; gap: 16px; margin-top: 26px; flex-wrap: wrap; justify-content: center; }
  .btn { border: none; font-weight: 800; font-size: clamp(14px,2vw,18px); letter-spacing: 1px;
    padding: 15px 34px; border-radius: 40px; cursor: pointer; }
  .btn.primary { background: var(--accent); color: #fff; }
  .btn.ghost { background: rgba(255,255,255,.1); color: #fff; border: 1px solid rgba(255,255,255,.25); }
  .btn.wa { background: #25d366; color: #07351b; }
  .deliver { background: rgba(6,4,10,.94); z-index: 28; }
  .deliver h2 { font-size: clamp(22px,3.4vw,34px); font-weight: 900; margin-bottom: 8px; }
  .deliver p { color: #cdbfe0; margin-bottom: 22px; }
  .badge { position: absolute; top: 12px; left: 12px; z-index: 40; background: rgba(0,0,0,.55);
    color: #ffd9c2; font-size: 11px; letter-spacing: 1px; padding: 5px 12px; border-radius: 20px;
    border: 1px solid rgba(255,138,61,.4); }
`;
